// src/lib/shopify-product-compat.js
//
// Minimal replacements for the old shopify-buy calls your code uses:
//   client.product.helpers.variantForOptions(product, selectedVariantLike)
//   client.product.fetch(productId)
//
// Env vars used (from your list):
//   GATSBY_SHOPIFY_ACCESS_TOKEN      -> Storefront access token
//   GATSBY_SHOPIFY_SHOP_URL          -> e.g. "ettespirits.myshopify.com" (or full https URL)
//   GATSBY_SHOPIFY_SHOP_NAME         -> fallback to "${name}.myshopify.com" if SHOP_URL missing
//   GATSBY_SHOPIFY_STOREFRONT_API_VERSION (optional) -> e.g. "2024-04"
// If the version isn’t set, we default to a safe, broadly supported one.

const normalizeDomain = raw => {
  if (!raw) return ''
  return raw.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
}

const token = process.env.GATSBY_SHOPIFY_ACCESS_TOKEN
const shopUrl = process.env.GATSBY_SHOPIFY_SHOP_URL
const shopName = process.env.GATSBY_SHOPIFY_SHOP_NAME
const domain = normalizeDomain(shopUrl || (shopName ? `${ shopName }.myshopify.com` : ''))

const apiVersion = process.env.GATSBY_SHOPIFY_STOREFRONT_API_VERSION || '2024-04'
const SF_URL = domain ? `https://${ domain }/api/${ apiVersion }/graphql.json` : ''

function eqSelectedOptions (a = [], b = []) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const an = a[i]?.name?.toLowerCase?.() || a[i]?.name
    const bn = b[i]?.name?.toLowerCase?.() || b[i]?.name
    const av = a[i]?.value ?? ''
    const bv = b[i]?.value ?? ''
    if (an !== bn || av !== bv) return false
  }
  return true
}

/**
 * Replacement for shopify-buy:
 *   client.product.helpers.variantForOptions(product, selectedVariantLike)
 */
export function variantForOptions (product, selectedVariantLike) {
  const wanted = selectedVariantLike?.selectedOptions || []
  const found = product?.variants?.find(v => eqSelectedOptions(v?.selectedOptions || [], wanted)) || null
  return found
}

/**
 * Replacement for shopify-buy:
 *   client.product.fetch(productId)
 * productId should be the GraphQL ID (same as product.shopifyId in your data).
 */
export async function fetchProductById (productId) {
  if (!domain || !token) {
    throw new Error('Shopify Storefront API env vars missing (GATSBY_SHOPIFY_ACCESS_TOKEN / GATSBY_SHOPIFY_SHOP_URL|GATSBY_SHOPIFY_SHOP_NAME)')
  }

  const query = `
    query ProductById($id: ID!) {
      product(id: $id) {
        id
        title
        variants(first: 100) {
          edges {
            node {
              id
              title
              availableForSale
              selectedOptions { name value }
            }
          }
        }
      }
    }
  `

  const res = await fetch(SF_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    },
    body: JSON.stringify({ query, variables: { id: productId } })
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.errors) {
    throw new Error(`Storefront product fetch failed: ${ JSON.stringify(json.errors || json) }`)
  }

  const p = json?.data?.product
  const variants = p?.variants?.edges?.map(({ node }) => ({
    id: node.id,
    shopifyId: node.id, // keep legacy field some code references
    title: node.title,
    availableForSale: node.availableForSale,
    selectedOptions: node.selectedOptions || []
  })) || []

  return {
    id: p?.id,
    shopifyId: p?.id,
    title: p?.title,
    variants
  }
}

/** Tiny object shaped like shopify-buy’s product namespace */
export const productCompat = {
  fetch: fetchProductById,
  helpers: { variantForOptions }
}
